import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import sfu from '../../assets/icons/sfu.svg';
import notification from '../../assets/icons/notification.svg';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import { SearchInput } from '../SearchInput/SearchInput';
import { SeasonBar } from '../Seasons/SeasonBar';
import { useAuth } from '../../context/AuthContext';
import { buildDisplayName } from '../../utils/userDisplay';
import { getRoleLabel, isAdminLikeRole, canReceiveYufunts } from '../../utils/roles';
import { searchAll } from '../../api/search';
import {
  getMyNotifications,
  getMyUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead
} from '../../api/notifications';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDidRequest, setSearchDidRequest] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const searchBlurTimeoutRef = useRef(null);
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);
  const { user, wallet, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = (e) => {
    e?.stopPropagation?.();
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    if (location.pathname !== '/search') {
      return;
    }
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get('q') || '');
  }, [location.pathname, location.search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (query.length < 2) return;
    setIsSearchActive(false);
    setSearchSuggestions([]);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchSuggestions([]);
      setSearchLoading(false);
      setSearchDidRequest(false);
      return;
    }

    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      setSearchDidRequest(true);
      try {
        const response = await searchAll(query, 5);
        const teamSuggestions = (response.teams || []).slice(0, 3).map((item) => ({
          key: `team-${item.team_id}`,
          title: item.name,
          meta: 'Команда',
          query: item.name,
          href: `/search/teams/${item.team_id}`,
        }));
        const userSuggestions = (response.users || []).slice(0, 3).map((item) => ({
          key: `user-${item.user_id}`,
          title: buildDisplayName(item) || `@${item.login}`,
          meta: `Пользователь · @${item.login}`,
          query: item.login,
          href: `/search/users/${item.user_id}`,
        }));
        const ordered = [...userSuggestions, ...teamSuggestions].slice(0, 6);
        setSearchSuggestions(ordered);
      } catch {
        setSearchSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    setIsSearchActive(false);
    setSearchSuggestions([]);
    setSearchDidRequest(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (searchBlurTimeoutRef.current) {
        window.clearTimeout(searchBlurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      const target = event.target;
      if (profileRef.current && !profileRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  const handleToggleNotifications = async () => {
    const next = !isNotificationsOpen;
    setIsNotificationsOpen(next);
    if (next) {
      await loadNotifications();
    }
  };

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const loadUnreadCount = async () => {
    try {
      const response = await getMyUnreadNotificationsCount();
      setUnreadCount(Number(response?.unread_count || 0));
    } catch {
      setUnreadCount(0);
    }
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await getMyNotifications({ unreadOnly: false, limit: 20, offset: 0 });
      setNotifications(response?.items || []);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    loadUnreadCount();
    const intervalId = setInterval(loadUnreadCount, 20000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isNotificationsOpen) return;
    loadNotifications();
    // eslint-disable-next-line
  }, [isNotificationsOpen]);

  const handleNotificationClick = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
    } catch {}
    await Promise.all([loadNotifications(), loadUnreadCount()]);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
    } catch {}
    await Promise.all([loadNotifications(), loadUnreadCount()]);
  };

  const isAdmin = isAdminLikeRole(user?.role);
  const canShowBalance = canReceiveYufunts(user?.role);
  const balanceLabel = location.pathname.startsWith('/transactions') ? (wallet?.balance ?? 0) : isAdmin ? '∞' : canShowBalance ? (wallet?.balance ?? 0) : '—';

  return (
    <header className={styles.header}>
      <SeasonBar compact />
      <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
        <SearchInput
          placeholder="Поиск команд или чего-то другого..."
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setIsSearchActive(true);
          }}
          onFocus={() => {
            if (searchBlurTimeoutRef.current) {
              window.clearTimeout(searchBlurTimeoutRef.current);
            }
            setIsSearchActive(true);
          }}
          onBlur={() => {
            searchBlurTimeoutRef.current = window.setTimeout(() => {
              setIsSearchActive(false);
            }, 120);
          }}
          loading={searchLoading}
          suggestions={searchSuggestions}
          showEmpty={!searchLoading && searchDidRequest && searchQuery.trim().length >= 2 && searchSuggestions.length === 0}
          emptyMessage="Ничего не найдено"
          active={isSearchActive}
          onSelectSuggestion={(item) => {
            const query = (item?.query || searchQuery).trim();
            if (!query) return;
            setIsSearchActive(false);
            setSearchQuery('');
            setSearchSuggestions([]);
            setSearchDidRequest(false);
            if (item?.href) {
              const divider = item.href.includes('?') ? '&' : '?';
              navigate(`${item.href}${divider}q=${encodeURIComponent(query)}`);
              return;
            }
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
        />
      </form>

      <div className={styles.rightBlock}>
        <div className={styles.balance}>
          <img src={sfu} alt="SFU" />
          <span className={styles.balanceValue}>{balanceLabel}</span>
        </div>

        <div className={styles.notificationWrap}>
          <button className={styles.notificationBtn} onClick={handleToggleNotifications} type="button">
            <img className={styles.notificationIcon} src={notification} alt="🔔" />
            {unreadCount > 0 ? <span className={styles.notificationBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
          </button>
          {isNotificationsOpen ? (
            <div className={styles.notificationsMenu} ref={notificationsRef}>
              <div className={styles.notificationsHeader}>
                <strong>Уведомления</strong>
                <button className={styles.markAllBtn} type="button" onClick={handleMarkAllRead}>
                  Прочитать все
                </button>
              </div>
              {notificationsLoading ? <p className={styles.notificationsEmpty}>Загрузка...</p> : null}
              {!notificationsLoading && notifications.length === 0 ? (
                <p className={styles.notificationsEmpty}>Пока уведомлений нет.</p>
              ) : null}
              {!notificationsLoading && notifications.map((item) => {
                const teamId = item?.kind === 'team.invite' ? item?.payload?.team_id : null;
                return (
                  <div
                    key={item.notification_id}
                    className={`${styles.notificationItem} ${item.is_read ? styles.readItem : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(item.notification_id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleNotificationClick(item.notification_id);
                    }}
                  >
                    <span className={styles.notificationTitle}>{item.title}</span>
                    <span className={styles.notificationMessage}>{item.message}</span>
                    {item.kind?.startsWith('auction.') && item.payload?.lot_id && <button
                      type="button" className={styles.notificationActionBtn}
                      onClick={(event) => { event.stopPropagation(); handleNotificationClick(item.notification_id); setIsNotificationsOpen(false); navigate(`/transactions/auction/${Number(item.payload.lot_id)}?season=${Number(item.payload.season_id)}`); }}
                    >Перейти к лоту</button>}
                    {teamId ? (
                      <button
                        className={styles.notificationActionBtn}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleNotificationClick(item.notification_id);
                          navigate('/team');
                        }}
                      >
                        Перейти к команде
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className={styles.profileBlock} ref={profileRef}>
          <button
            className={styles.profileBtn}
            onClick={toggleMenu}
            type="button"
          >
            <img
              src={user?.avatar_url || avatarPlaceholder}
              alt="Аватар"
              className={styles.avatar}
              onError={(event) => {
                event.currentTarget.src = avatarPlaceholder;
              }}
            />
            <div className={styles.userInfo}>
              <span className={styles.userName}>{buildDisplayName(user) || 'Пользователь'}</span>
              <span className={styles.userRole}>@{user?.login || 'user'} · {getRoleLabel(user?.role || 'MEMBER')}</span>
            </div>
            <span className={styles.arrowWrapper}>
              <svg
                className={`${styles.arrow} ${isMenuOpen ? styles.arrowUp : ''}`}
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
              >
                <path
                  d="M1 1L6 6L11 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>

          {isMenuOpen && (
            <div className={styles.dropdownMenu}>
              <button
                className={styles.menuBtn}
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/profile');
                }}
              >
                Мой профиль
              </button>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                Выйти
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
